import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stack, Group, Text, Badge, Button, Avatar } from '@repletoryreach/sdk-ui';
import {
  Alert,
  DataTable,
  EmptyState,
  EntityCardGrid,
  KpiFilterRow,
  PageHeader,
  StatusBadge,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { admin } from '@repletoryreach/sdk-api';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { usePersistedState } from '../../lib/usePersistedState';
import { formatDateTime, formatInteger, formatRelative } from '../../lib/format';
import type { Indexed } from '../../lib/types';
import { TenantName } from '../../components/TenantName';
import { TwoLineCell } from '../../components/TwoLineCell';
import { CopyableId } from '../../components/CopyableId';
import { ActiveFilterPills, type ActiveFilterPill } from '../../components/ActiveFilterPills';
import { TableFooter } from '../../components/TableFooter';
import { RowMenu } from '../../components/RowMenu';
import { useTenantMap } from '../../data/useTenants';
import { ConversationDrawer } from './components/ConversationDrawer';
import { ALL_STATES, stateOf, STATE_LABEL_KEYS, STATE_TONE } from './state';
import { useT } from '../../i18n/I18nProvider';
import { whatsAppPreview } from '@repletoryreach/sdk-utils/whatsapp-text';

const MUTED = 'var(--rr-color-text-muted)';

/**
 * The server clamps this feed and returns no total, so the page can only ever
 * speak for the rows it holds. The constant is named so the label that admits
 * the truncation and the request that causes it cannot drift apart.
 */
const FEED_LIMIT = 200;

type Conversation = admin.AdminConversation;
type KpiKey = 'all' | 'open' | 'unread' | 'unassigned' | 'recent';

/**
 * Two letters for the avatar. An unnamed contact used to render the first two
 * characters of the msisdn, which is a country-code fragment like "+2" and
 * says nothing; the last two digits at least differ between contacts.
 */
function initials(name: string | null, msisdn: string): string {
  if (name?.trim()) {
    const words = name.trim().split(/\s+/);
    return (words.length > 1 ? `${words[0][0]}${words[1][0]}` : words[0].slice(0, 2)).toUpperCase();
  }
  const digits = msisdn.replace(/\D/g, '');
  return digits.slice(-2) || msisdn.slice(0, 2);
}

/**
 * The thread's state, or nothing at all when the feed did not carry one. A
 * silent cell is better than a guessed badge: whether a thread is still open
 * is exactly what an operator comes to this column to learn.
 */
function ConversationState({ row }: { row: Conversation }) {
  const t = useT();
  const state = stateOf(row);
  if (!state) return <Text role="caption" c={MUTED}>{t('common.notAvailable')}</Text>;
  return <StatusBadge status={STATE_TONE[state]} size="xs">{t(STATE_LABEL_KEYS[state])}</StatusBadge>;
}

export default function ConversationsPage() {
  const t = useT();
  const nav = useNavigate();
  const { map: tenants } = useTenantMap();

  const [view, setView] = usePersistedState<ViewMode>('admin.conversations.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.conversations.pageSize', 25);

  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<KpiKey | null>('all');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Conversation | null>(null);

  // The organisation narrows the request rather than the already-truncated
  // page, so picking a tenant yields that tenant's newest 200 conversations
  // instead of whichever of its threads happened to survive the platform-wide
  // cut. The endpoint has always accepted `org_id`; nothing sent it.
  const orgFilter = typeof filters.organisation === 'string' && filters.organisation ? filters.organisation : undefined;
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'conversations', { org_id: orgFilter, limit: FEED_LIMIT }],
    queryFn: () => admin.listConversations({ org_id: orgFilter, limit: FEED_LIMIT }),
    staleTime: 30_000
  });
  const all = useMemo(() => (data?.data ?? []) as Conversation[], [data]);

  // Recomputed on every fetch. Frozen at mount, "Active (24h)" quietly meant
  // "active in the 24 hours before this tab was opened", which on an overnight
  // dashboard is a different question from the one the chip asks.
  const recentCutoff = useMemo(() => Date.now() - 24 * 3600 * 1000, [data]);
  const counts = useMemo(() => ({
    all: all.length,
    open: all.filter((r) => stateOf(r) === 'open').length,
    unread: all.filter((r) => r.unread_count > 0).length,
    unassigned: all.filter((r) => !r.assigned_agent_user_id).length,
    recent: all.filter((r) => new Date(r.last_message_at).getTime() >= recentCutoff).length
  }), [all, recentCutoff]);

  const kpiItems: KpiFilterItem[] = [
    { id: 'all', label: t('conversations.kpi.all'), value: counts.all, tone: 'primary' },
    { id: 'open', label: t('conversations.kpi.open'), value: counts.open, tone: 'pending' },
    { id: 'unread', label: t('conversations.kpi.unread'), value: counts.unread, tone: 'warning' },
    { id: 'unassigned', label: t('conversations.kpi.unassigned'), value: counts.unassigned, tone: 'pending' },
    { id: 'recent', label: t('conversations.kpi.recent'), value: counts.recent, tone: 'healthy' }
  ];

  const filterFields: FilterField[] = useMemo(() => {
    // Options come from the tenants list, by name, rather than from the loaded
    // rows: the filter is served by the API now, so deriving the choices from
    // the response would collapse the list to whichever org is selected.
    const orgs = Array.from(tenants.values())
      .map((tenant) => ({ value: tenant.id, label: tenant.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      {
        key: 'organisation',
        label: t('conversations.filters.organisation'),
        placeholder: t('conversations.filters.organisationPlaceholder'),
        options: orgs
      },
      {
        key: 'state',
        label: t('conversations.filters.state'),
        placeholder: t('conversations.filters.statePlaceholder'),
        options: ALL_STATES.map((code) => ({ value: code, label: t(STATE_LABEL_KEYS[code]) }))
      },
      {
        key: 'assignment',
        label: t('conversations.filters.assignment'),
        placeholder: t('conversations.filters.assignmentPlaceholder'),
        options: [
          { value: 'assigned', label: t('conversations.filters.assignment.assigned') },
          { value: 'unassigned', label: t('conversations.filters.assignment.unassigned') }
        ]
      }
    ];
  }, [tenants, t]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters =
    Boolean(query.trim()) || (kpi !== null && kpi !== 'all') || drawerActive > 0;

  function clearAllFilters() {
    setQuery('');
    setKpi('all');
    setFilters({});
    setPage(1);
  }

  // Each narrowing, named and removable. The organisation pill resolves to the
  // tenant's name rather than showing the id the request actually carries.
  const pills: ActiveFilterPill[] = [];
  if (query.trim()) pills.push({ id: 'query', field: t('common.search'), value: query.trim() });
  if (kpi && kpi !== 'all') {
    pills.push({ id: 'kpi', field: t('common.view'), value: kpiItems.find((i) => i.id === kpi)?.label ?? kpi });
  }
  if (filters.organisation) {
    pills.push({
      id: 'organisation',
      field: t('conversations.filters.organisation'),
      value: tenants.get(filters.organisation)?.name ?? t('tenantName.unknownShort')
    });
  }
  if (filters.state) {
    const code = filters.state as keyof typeof STATE_LABEL_KEYS;
    pills.push({ id: 'state', field: t('conversations.filters.state'), value: STATE_LABEL_KEYS[code] ? t(STATE_LABEL_KEYS[code]) : filters.state });
  }
  if (filters.assignment) {
    pills.push({
      id: 'assignment',
      field: t('conversations.filters.assignment'),
      value: t(`conversations.filters.assignment.${filters.assignment}`)
    });
  }

  function removePill(id: string) {
    setPage(1);
    if (id === 'query') return setQuery('');
    if (id === 'kpi') return setKpi('all');
    setFilters((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  const filtered = useMemo(() => {
    let rows = all;
    // The endpoint takes no `state` parameter, so this one filter stays on the
    // client and reads only the rows the cap allowed through.
    if (kpi === 'open') rows = rows.filter((r) => stateOf(r) === 'open');
    if (kpi === 'unread') rows = rows.filter((r) => r.unread_count > 0);
    if (kpi === 'unassigned') rows = rows.filter((r) => !r.assigned_agent_user_id);
    if (kpi === 'recent') rows = rows.filter((r) => new Date(r.last_message_at).getTime() >= recentCutoff);
    if (filters.state) rows = rows.filter((r) => stateOf(r) === filters.state);
    if (filters.assignment === 'assigned') rows = rows.filter((r) => !!r.assigned_agent_user_id);
    if (filters.assignment === 'unassigned') rows = rows.filter((r) => !r.assigned_agent_user_id);
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        r.contact_msisdn.toLowerCase().includes(q) ||
        (r.contact_display_name ?? '').toLowerCase().includes(q) ||
        whatsAppPreview(r.last_message_preview ?? '').toLowerCase().includes(q) ||
        (tenants.get(r.organisation_id)?.name ?? '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [all, kpi, filters, query, recentCutoff, tenants]);

  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1);
  }, [filtered.length, page, pageSize]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const capped = all.length >= FEED_LIMIT;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.section.messaging')}
        title={<>{t('conversations.title')} <Badge size="sm" variant="light" color="gray">{t('common.readOnly')}</Badge></>}
        description={t('conversations.description')}
        actions={
          <Stack gap="xs" align="flex-end">
            <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>
              {t('action.refresh')}
            </Button>
            <Text role="caption" c={MUTED}>{t('common.lastUpdated', { time: formatDateTime(new Date(dataUpdatedAt).toISOString()) })}</Text>
          </Stack>
        }
      />

      {/* The chips carry the shape of the feed and sit on the page ground,
          above the table rather than inside its chrome. */}
      <KpiFilterRow items={kpiItems} value={kpi} onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }} />
      <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />

      <TableShell
        title={t('conversations.tableTitle')}
        totalLabel={
          capped
            ? t('conversations.totalLabelCapped', { shown: filtered.length, total: all.length })
            : t('conversations.totalLabel', { shown: filtered.length, total: all.length })
        }
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('conversations.searchPlaceholder')}
        filters={
          <Group gap="sm" wrap="nowrap">
            <FilterDrawer fields={filterFields} values={filters} onApply={(next) => { setFilters(next); setPage(1); }} />
          </Group>
        }
        view={view}
        onViewChange={setView}
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={<EmptyState title={t('conversations.empty')} description={hasActiveFilters ? t('conversations.emptyFiltered') : t('conversations.emptyHint')} action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined} />}
        error={error
          ? (
            <Alert color="error" title={t('conversations.loadError')}>
              <Group gap="sm">
                <Text role="bodySmall">{error instanceof Error ? error.message : t('common.unknownError')}</Text>
                <Button size="xs" variant="outline" onClick={() => void refetch()}>{t('action.retry')}</Button>
              </Group>
            </Alert>
          )
          : null}
        pagination={
          <Stack gap="sm" style={{ width: '100%' }}>
            {/* The cap sits with the pager: it is the sentence that says how
                much of the platform these page numbers cover. */}
            {capped && <Alert color="info" variant="light">{t('conversations.capHint')}</Alert>}
            <TableFooter
              page={page}
              pageSize={pageSize}
              total={filtered.length}
              capped={capped}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            />
          </Stack>
        }
      >
        {view === 'table' && (
          <DataTable<Indexed<Conversation>>
            records={paged as Indexed<Conversation>[]}
            loading={isLoading}
            idAccessor="id"
            emptyState={t('conversations.emptyHint')}
            onRowClick={(r) => setSelected(r as Conversation)}
            columns={[
              {
                accessor: 'contact_msisdn',
                title: t('conversations.columns.contact'),
                // The contact leads the row, with the number under the name:
                // the name is how a person recognises the thread, the number
                // is how a ticket refers to it.
                render: (r) => (
                  <TwoLineCell
                    primary={r.contact_display_name ?? r.contact_msisdn}
                    secondary={r.contact_display_name ? r.contact_msisdn : undefined}
                    leading={
                      <Avatar size="sm" radius="md" style={{ background: 'color-mix(in srgb, var(--rr-color-primary) 16%, transparent)', color: 'var(--rr-color-primary)', fontWeight: 700 }}>
                        {initials(r.contact_display_name, r.contact_msisdn)}
                      </Avatar>
                    }
                  />
                )
              },
              {
                accessor: 'organisation_id',
                title: t('conversations.columns.tenant'),
                render: (r) => <TenantName id={r.organisation_id} />
              },
              {
                // Operators paste this into Meta's console when a number
                // misbehaves, so it is a value worth quoting rather than
                // bookkeeping.
                accessor: 'phone_number_id',
                title: t('conversations.columns.phoneNumberId'),
                render: (r) => <CopyableId value={r.phone_number_id} label={t('conversations.copy.phoneNumberId')} chars={12} />
              },
              {
                accessor: 'state',
                title: t('conversations.columns.state'),
                render: (r) => <ConversationState row={r as Conversation} />
              },
              {
                accessor: 'assigned_agent_user_id',
                title: t('conversations.columns.agent'),
                render: (r) => r.assigned_agent_user_id
                  ? <Text role="bodySmall">{t('conversations.assigned')}</Text>
                  : <Text role="caption" c={MUTED}>{t('conversations.unassigned')}</Text>
              },
              {
                accessor: 'unread_count',
                title: t('conversations.columns.unread'),
                textAlign: 'right',
                render: (r) => r.unread_count > 0
                  ? <Badge size="xs" color="orange" variant="filled">{formatInteger(r.unread_count)}</Badge>
                  : <Text role="caption" c={MUTED}>{formatInteger(0)}</Text>
              },
              {
                accessor: 'last_message_preview',
                title: t('conversations.columns.preview'),
                render: (r) => r.last_message_preview
                  // A clamp rather than a fixed 320px of nowrap: the column then
                  // gives its width back on a narrow screen instead of forcing
                  // the whole table into horizontal scroll.
                  ? (
                    <span title={whatsAppPreview(r.last_message_preview)}>
                      <Text role="bodySmall" lineClamp={2} style={{ maxWidth: 'min(40ch, 100%)' }}>{whatsAppPreview(r.last_message_preview)}</Text>
                    </span>
                  )
                  : <Text role="caption" c={MUTED}>{t('conversations.noPreview')}</Text>
              },
              {
                accessor: 'last_message_at',
                title: t('conversations.columns.lastActivity'),
                sortable: true,
                render: (r) => <TwoLineCell primary={formatRelative(r.last_message_at)} secondary={formatDateTime(r.last_message_at)} />
              },
              {
                accessor: 'actions',
                title: t('common.actions'),
                width: 64,
                textAlign: 'right',
                render: (r) => (
                  <RowMenu
                    actions={[
                      { id: 'view', label: t('conversations.actions.view'), onSelect: () => setSelected(r as Conversation) },
                      { id: 'tenant', label: t('conversations.actions.openTenant'), onSelect: () => nav(`/tenants/${r.organisation_id}`) }
                    ]}
                  />
                )
              }
            ]}
          />
        )}
        {view === 'list' && (
          <Stack gap="sm">
            {paged.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setSelected(r)}
                className="rr-list-row"
              >
                <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
                  <Avatar size="md" radius="md" style={{ background: 'color-mix(in srgb, var(--rr-color-primary) 16%, transparent)', color: 'var(--rr-color-primary)', fontWeight: 700, flexShrink: 0 }}>
                    {initials(r.contact_display_name, r.contact_msisdn)}
                  </Avatar>
                  <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                    <Group gap="sm" align="baseline" wrap="nowrap">
                      <Text role="subtitle" fw={600} truncate>{r.contact_display_name ?? r.contact_msisdn}</Text>
                      <TenantName id={r.organisation_id} subtle />
                    </Group>
                    <Text role="caption" c={MUTED} truncate>
                      {r.last_message_preview ?? t('conversations.noPreview')}
                    </Text>
                  </Stack>
                  <span style={{ flexShrink: 0 }}><ConversationState row={r} /></span>
                  {r.unread_count > 0 && (
                    <Badge size="sm" color="orange" variant="filled" style={{ flexShrink: 0 }}>
                      {formatInteger(r.unread_count)}
                    </Badge>
                  )}
                  <Text role="caption" c={MUTED} style={{ flexShrink: 0 }}>
                    {formatRelative(r.last_message_at)}
                  </Text>
                </Group>
              </button>
            ))}
          </Stack>
        )}
        {view === 'cards' && (
          <EntityCardGrid<Conversation>
            records={paged}
            idAccessor="id"
            cols={3}
            spacing="md"
            onCardClick={(r) => setSelected(r)}
            renderCard={(r) => (
              <Stack gap="md">
                <Group gap="sm" align="center" wrap="nowrap">
                  <Avatar size="md" radius="md" style={{ background: 'color-mix(in srgb, var(--rr-color-primary) 16%, transparent)', color: 'var(--rr-color-primary)', fontWeight: 700 }}>
                    {initials(r.contact_display_name, r.contact_msisdn)}
                  </Avatar>
                  <Stack gap="xs" style={{ minWidth: 0 }}>
                    <Text role="subtitle" fw={600} truncate>{r.contact_display_name ?? r.contact_msisdn}</Text>
                    <TenantName id={r.organisation_id} subtle />
                  </Stack>
                </Group>
                <Text role="bodySmall" lineClamp={2} style={{ minHeight: 36 }}>
                  {r.last_message_preview ?? t('conversations.noPreview')}
                </Text>
                <Group justify="space-between" align="center" style={{ paddingTop: 12, borderTop: '1px solid var(--rr-color-border)' }}>
                  <Group gap="sm" align="center">
                    <ConversationState row={r} />
                    <Text role="caption" c={MUTED}>{formatRelative(r.last_message_at)}</Text>
                  </Group>
                  {r.unread_count > 0 && <Badge size="xs" color="orange" variant="filled">{t('conversations.unreadCount', { count: r.unread_count })}</Badge>}
                </Group>
              </Stack>
            )}
          />
        )}
      </TableShell>
      <ConversationDrawer row={selected} onClose={() => setSelected(null)} />
    </Stack>
  );
}
